import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  deleteReport,
  fetchReports,
  regenerateReport,
  syncReports,
  type ReportView,
  type ReportsList,
} from '../api/reports';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { MatchArmament } from './MatchArmament';
import { KNOWN_REPORT_STATUSES, reportStatusLabel, reportStatusText } from './reportStatus';
import { useConfirm } from './useConfirm';
import { Shell } from './Shell';
import { PageLoading } from './PageStates';
import { useRetryKey } from './connectionHelpers';
import { errorText } from './useSessionLive';
import { fmtMoment } from './dates';

type SortKey = 'mission' | 'server' | 'when' | 'duration' | 'players' | 'kills';
type Dir = 'asc' | 'desc';

/** `num` marks the columns whose cells hold a number, right-aligned and tabular. */
const COLUMNS: { key: SortKey; label: string; num?: boolean }[] = [
  { key: 'mission', label: ca.reportsPage.colMission },
  { key: 'server', label: ca.reportsPage.colServer },
  { key: 'when', label: ca.reportsPage.colWhen },
  { key: 'duration', label: ca.reportsPage.colDuration, num: true },
  { key: 'players', label: ca.reportsPage.colPlayers, num: true },
  { key: 'kills', label: ca.reportsPage.colKills, num: true },
];

/**
 * What the row is called: the mission's title where the platform knows it,
 * never the file the panel knows it by (`ops_2xqvgqb`). An older platform sends
 * no title and the panel's name is all there is.
 */
const titleOf = (r: ReportView) => r.displayTitle ?? r.missionName ?? '?';

/**
 * A night nobody played: the panel discarded it, or it ended with no player on
 * its roster. Most of the list on a server that restarts a lot, and none of it
 * a report anybody reads, so the page leaves them out until asked. A match
 * still running is never one: its players are still arriving.
 */
const isDiscarded = (r: ReportView) =>
  r.status === 'DISCARDED' || (r.status !== 'LIVE' && (r.playerCount ?? 0) === 0);

function compare(a: ReportView, b: ReportView, key: SortKey): number {
  switch (key) {
    case 'mission':
      return titleOf(a).localeCompare(titleOf(b), 'ca');
    case 'server':
      return (a.serverName ?? '').localeCompare(b.serverName ?? '', 'ca');
    case 'when':
      return (a.startedAt ?? '').localeCompare(b.startedAt ?? '');
    case 'duration':
      return (a.durationSeconds ?? 0) - (b.durationSeconds ?? 0);
    case 'players':
      return (a.playerCount ?? 0) - (b.playerCount ?? 0);
    case 'kills':
      return (a.totalKills ?? 0) - (b.totalKills ?? 0);
  }
}

/** The filters of the page, read from and written to the URL so a filtered list is a shareable link. */
interface Filters {
  q: string;
  server: string;
  status: string;
  from: string;
  to: string;
  mine: boolean;
  /** The nights nobody played are listed too. */
  discarded: boolean;
}

function readFilters(params: URLSearchParams): Filters {
  return {
    q: params.get('q') ?? '',
    server: params.get('server') ?? '',
    status: params.get('status') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    mine: params.get('mine') === '1',
    discarded: params.get('discarded') === '1',
  };
}

function matches(r: ReportView, f: Filters): boolean {
  if (f.q) {
    const needle = f.q.toLowerCase();
    const hay = `${titleOf(r)} ${r.missionName ?? ''} ${r.world ?? ''}`.toLowerCase();
    if (!hay.includes(needle)) return false;
  }
  if (f.server && (r.serverName ?? '') !== f.server) return false;
  if (f.status && (r.status ?? '').toUpperCase() !== f.status.toUpperCase()) return false;
  if (f.from || f.to) {
    if (!r.startedAt) return false;
    const at = new Date(r.startedAt).getTime();
    if (f.from && at < new Date(`${f.from}T00:00`).getTime()) return false;
    if (f.to && at > new Date(`${f.to}T23:59:59.999`).getTime()) return false;
  }
  if (f.mine && !r.played) return false;
  // Asking for the discarded ones by their status is asking to see them.
  if (!f.discarded && f.status !== 'DISCARDED' && isDiscarded(r)) return false;
  return true;
}

/**
 * Every match report the panel has (docs/design/ASM_INTEGRATION_STUDY.md,
 * 3.4), as a tool: filters in the URL, sortable columns, the rows the member
 * played marked, and for admins delete and regenerate.
 */
export function Reports() {
  const { me } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<ReportsList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, confirmDialog] = useConfirm();
  const [notice, setNotice] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: Dir }>({ key: 'when', dir: 'desc' });
  const t = ca.reportsPage2;
  /** The report whose armament tables are open; only one at a time. */
  const [open, setOpen] = useState<number | null>(null);
  const filters = useMemo(() => readFilters(params), [params]);

  const load = useCallback(
    () =>
      fetchReports()
        .then((d) => {
          setData(d);
          setError(null);
        })
        .catch((e) => setError(errorText(e))),
    [],
  );

  const [attempt] = useRetryKey();
  useEffect(() => {
    if (me.authenticated) void load();
  }, [me.authenticated, load, attempt]);

  const setFilter = (key: keyof Filters, value: string | boolean) => {
    const next = new URLSearchParams(params);
    const v = value === true ? '1' : value === false ? '' : value;
    if (v) next.set(key, v);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const anyFilter = filters.q || filters.server || filters.status || filters.from || filters.to;

  const servers = useMemo(
    () =>
      Array.from(
        new Set((data?.reports ?? []).map((r) => r.serverName).filter((s): s is string => !!s)),
      ).sort((a, b) => a.localeCompare(b, 'ca')),
    [data],
  );
  /** The statuses the web app names, then any other the panel sent, so every row can be found. */
  const statuses = useMemo(() => {
    const seen = new Set(KNOWN_REPORT_STATUSES);
    const extra = new Set<string>();
    for (const r of data?.reports ?? []) if (r.status && !seen.has(r.status)) extra.add(r.status);
    return [...KNOWN_REPORT_STATUSES, ...[...extra].sort()];
  }, [data]);
  const rows = useMemo(() => {
    const list = (data?.reports ?? []).filter((r) => matches(r, filters));
    list.sort((a, b) => (sort.dir === 'asc' ? 1 : -1) * compare(a, b, sort.key));
    return list;
  }, [data, filters, sort]);
  // How many the list leaves out for being nobody's night, the other filters applied.
  const discardedCount = useMemo(
    () =>
      (data?.reports ?? []).filter(
        (r) => isDiscarded(r) && matches(r, { ...filters, discarded: true }),
      ).length,
    [data, filters],
  );
  const playedCount = rows.filter((r) => r.played).length;
  const playedHours = rows.reduce((sum, r) => sum + (r.durationSeconds ?? 0), 0) / 3600;

  const toggleSort = (key: SortKey) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'when' ? 'desc' : 'asc' },
    );

  if (!me.authenticated) {
    return (
      <Shell title={ca.reports}>
        <p>
          <Link to="/signin">{ca.signIn}</Link>
        </p>
      </Shell>
    );
  }

  const isAdmin = me.roles.includes('ADMIN');
  const canManage = !!data?.canManage && isAdmin;

  async function removeReport(id: number) {
    if (
      !(await confirm({ ...ca.reportDeleteConfirm, confirmLabel: ca.reportDelete, danger: true }))
    )
      return;
    try {
      await deleteReport(id);
      await load();
    } catch (e) {
      setError(errorText(e));
    }
  }

  return (
    <Shell
      title={ca.reports}
      actions={
        isAdmin && (
          <button
            type="button"
            onClick={() =>
              void syncReports()
                .then((s) => {
                  setNotice(ca.reportsSynced(s.matches, s.rostersFilled));
                  void load();
                })
                .catch((e) => setError(errorText(e)))
            }
          >
            <i className="bi bi-arrow-repeat" aria-hidden="true" />
            {ca.reportsSync}
          </button>
        )
      }
    >
      {confirmDialog}
      <p className="muted small">{ca.reportsIntro}</p>
      {!data && !error && <PageLoading />}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="notice ok" role="status">
          {notice}
        </p>
      )}
      {data && data.syncedAt && (
        <p className="muted small">{ca.reportsLastSync(fmtMoment(data.syncedAt))}</p>
      )}
      {data && data.reports.length === 0 && (
        <div className="empty">
          <i className="bi bi-journal-text" aria-hidden="true" />
          <p className="muted">{ca.reportsEmpty}</p>
        </div>
      )}
      {data && data.reports.length > 0 && (
        <>
          <div className="filters reports-filters">
            <label>
              {t.search}
              <input
                type="search"
                value={filters.q}
                placeholder={t.searchPlaceholder}
                onChange={(e) => setFilter('q', e.target.value)}
              />
            </label>
            <label>
              {t.server}
              <select value={filters.server} onChange={(e) => setFilter('server', e.target.value)}>
                <option value="">{t.allServers}</option>
                {servers.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t.status}
              <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)}>
                <option value="">{t.allStatuses}</option>
                {statuses.map((s) => (
                  <option key={s} value={s}>
                    {reportStatusText(s)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t.from}
              <input
                type="date"
                value={filters.from}
                onChange={(e) => setFilter('from', e.target.value)}
              />
            </label>
            <label>
              {t.to}
              <input
                type="date"
                value={filters.to}
                onChange={(e) => setFilter('to', e.target.value)}
              />
            </label>
            <button
              type="button"
              className={`chip toggle ${filters.mine ? 'on' : ''}`}
              aria-pressed={filters.mine}
              onClick={() => setFilter('mine', !filters.mine)}
            >
              <i className="bi bi-person-check" aria-hidden="true" />
              {t.onlyPlayed}
            </button>
            {(discardedCount > 0 || filters.discarded) && filters.status !== 'DISCARDED' && (
              <button
                type="button"
                className={`chip toggle ${filters.discarded ? 'on' : ''}`}
                aria-pressed={filters.discarded}
                title={t.showDiscardedHint}
                onClick={() => setFilter('discarded', !filters.discarded)}
              >
                <i className="bi bi-eye-slash" aria-hidden="true" />
                {t.showDiscarded(discardedCount)}
              </button>
            )}
            {(anyFilter || filters.mine || filters.discarded) && (
              <button
                type="button"
                className="ghost small"
                onClick={() => setParams(new URLSearchParams(), { replace: true })}
              >
                {t.clearFilters}
              </button>
            )}
          </div>
          <dl className="stats reports-stats" data-testid="reports-summary">
            <div className="stat">
              <dt>{t.stats.matches}</dt>
              <dd>{rows.length}</dd>
            </div>
            <div className="stat">
              <dt>{t.stats.played}</dt>
              <dd>{playedCount}</dd>
            </div>
            {playedHours > 0 && (
              <div className="stat">
                <dt>{t.stats.hours}</dt>
                <dd>{playedHours.toLocaleString('ca', { maximumFractionDigits: 1 })}</dd>
              </div>
            )}
          </dl>
          {rows.length === 0 ? (
            <div className="empty">
              <i className="bi bi-funnel" aria-hidden="true" />
              <p className="muted">{t.noMatch}</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data reports">
                <thead>
                  <tr>
                    {COLUMNS.map((c) => {
                      const on = sort.key === c.key;
                      return (
                        <th
                          key={c.key}
                          className={c.num ? 'num' : undefined}
                          aria-sort={
                            on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'
                          }
                        >
                          <button
                            type="button"
                            className={`sort ${on ? 'on' : ''}`}
                            onClick={() => toggleSort(c.key)}
                            title={t.sortBy(c.label)}
                          >
                            {c.label}
                            {on && (
                              <i
                                className={`bi ${sort.dir === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill'}`}
                                aria-hidden="true"
                              />
                            )}
                          </button>
                        </th>
                      );
                    })}
                    <th className="actions">{ca.reportsPage.colActions}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <Fragment key={r.id}>
                      <tr className={r.played ? 'mine' : undefined}>
                        <td data-label={ca.reportsPage.colMission} className="mission">
                          <div className="row">
                            {/*
                            The title is what is read; the file the panel knows
                            the mission by stays on it as a hint, for whoever
                            has to find it there.
                          */}
                            {r.reportUrl ? (
                              <a
                                className="mission-link"
                                href={r.reportUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <strong title={r.missionName ?? undefined}>{titleOf(r)}</strong>
                              </a>
                            ) : (
                              <strong title={r.missionName ?? undefined}>{titleOf(r)}</strong>
                            )}
                            {r.world && <span className="chip">{r.world}</span>}
                            {r.status && (
                              <span
                                className={`chip ${r.status === 'LIVE' ? 'ok' : r.status === 'DISCARDED' ? 'bad' : ''}`}
                              >
                                {reportStatusLabel(r.status).label}
                                {reportStatusLabel(r.status).raw !== null && (
                                  <small className="muted status-raw"> ({r.status})</small>
                                )}
                              </span>
                            )}
                            {r.played && (
                              <span className="chip played">
                                <i className="bi bi-check2" aria-hidden="true" />
                                {t.played}
                              </span>
                            )}
                          </div>
                        </td>
                        <td data-label={ca.reportsPage.colServer} className="server">
                          {r.serverName ?? ''}
                        </td>
                        <td data-label={ca.reportsPage.colWhen} className="muted small when">
                          {r.startedAt ? fmtMoment(r.startedAt) : ''}
                        </td>
                        <td data-label={ca.reportsPage.colDuration} className="num">
                          {ca.reportDuration(r.durationSeconds ?? 0)}
                        </td>
                        <td data-label={ca.reportsPage.colPlayers} className="num">
                          {r.playerCount ?? 0}
                        </td>
                        <td data-label={ca.reportsPage.colKills} className="num">
                          {r.totalKills ?? 0}
                          {r.totalFriendlyKills ? (
                            <span className="muted small" title={ca.reportsPage.colKills}>
                              {' '}
                              ({r.totalFriendlyKills})
                            </span>
                          ) : null}
                        </td>
                        <td data-label={ca.reportsPage.colActions} className="actions">
                          <div className="row actions">
                            {/*
                            The one action everybody comes for, said in a word;
                            the rest are icons with their name on hover and for
                            a screen reader.
                          */}
                            {r.reportUrl && (
                              <a
                                className="button small"
                                href={r.reportUrl}
                                target="_blank"
                                rel="noreferrer"
                                title={t.openReportOf(titleOf(r))}
                              >
                                <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                                {ca.reportOpen}
                              </a>
                            )}
                            {/*
                            The armament tables the panel already counts. Loaded
                            when the row is opened and not before: a season of
                            reports would otherwise be one request to the panel
                            per night on the list.
                          */}
                            <button
                              type="button"
                              className={`small icon ghost ${open === r.id ? 'on' : ''}`}
                              aria-expanded={open === r.id}
                              title={open === r.id ? t.detailClose : t.detail}
                              aria-label={open === r.id ? t.detailClose : t.detailOpen(titleOf(r))}
                              onClick={() => setOpen(open === r.id ? null : r.id)}
                            >
                              <i className="bi bi-crosshair" aria-hidden="true" />
                            </button>
                            {r.pdfUrl && (
                              <a
                                className="button small icon ghost"
                                href={r.pdfUrl}
                                target="_blank"
                                rel="noreferrer"
                                title={ca.reportPdf}
                                aria-label={ca.reportPdf}
                              >
                                <i className="bi bi-file-earmark-pdf" aria-hidden="true" />
                              </a>
                            )}
                            {canManage && r.status !== 'LIVE' && (
                              <>
                                <button
                                  type="button"
                                  className="small icon ghost"
                                  title={ca.reportRegenerate}
                                  aria-label={ca.reportRegenerate}
                                  onClick={() =>
                                    void regenerateReport(r.id)
                                      .then(() => setNotice(ca.reportRegenerate))
                                      .catch((e) => setError(errorText(e)))
                                  }
                                >
                                  <i className="bi bi-arrow-clockwise" aria-hidden="true" />
                                </button>
                                {/*
                                Not a red button on every row of the list: it
                                is quiet until the pointer or the focus is on it.
                              */}
                                <button
                                  type="button"
                                  className="small icon ghost quiet-danger"
                                  title={ca.reportDelete}
                                  aria-label={ca.reportDelete}
                                  onClick={() => void removeReport(r.id)}
                                >
                                  <i className="bi bi-trash" aria-hidden="true" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {open === r.id && (
                        <tr className="armament-row">
                          <td colSpan={7}>
                            <MatchArmament id={r.id} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Shell>
  );
}
